"use client";
//import node module libraries
import { useState, useEffect } from "react";
// import custom shadcn UI components
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { formatFileName, formatString } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

// import custom hooks
import toast from "react-hot-toast";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  getColumnsDescription,
  updateColumns,
} from "@/lib/api-client";

import { Table as TableIcon } from "lucide-react";

interface AddTableSchemaProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Columns {
  column_name: string;
  data_type: string;
  column_description: string;
}

interface UpdateColumnsProps {
  updated_table_description: string;
  columns: Columns[];
}

const AddTableSchema: React.FC<AddTableSchemaProps> = ({ isOpen, onClose }) => {
  const [columns, setColumns] = useState([
    {
      column_name: "",
      data_type: "",
      column_description: "",
    },
  ]);
  const [description, setDescription] = useState("");
  const [tableName, setTableName] = useState<string>("");

  // Fetch column description when dialog opens (on click of "Column Description")
  const {
    data: tableData,
    isLoading,
    isFetching,
    error: queryError,
  } = useQuery({
    queryKey: ["table_data", isOpen],
    queryFn: getColumnsDescription,
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (tableData?.result) {
      const { result } = tableData;
      setDescription(result?.table_description || "");
      // API returns column_desciption (typo); normalize to column_description
      const normalizedColumns = (result?.columns || []).map((col: Record<string, string>) => ({
        column_name: col.column_name ?? "",
        data_type: col.data_type ?? "",
        column_description: col.column_description ?? col.column_desciption ?? "",
      }));
      setColumns(normalizedColumns);
      setTableName(result?.table_name || "");
    }
  }, [tableData]);

  const {
    mutate: updateColumnsFn,
    isPending,
    error: mutationError,
    isError,
  } = useMutation({
    mutationFn: async (data: UpdateColumnsProps) => updateColumns(data),
    onSuccess: () => {
      toast.success("Columns updated successfully!", {
        position: "bottom-right",
      });
      onClose();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to update columns", {
        position: "bottom-right",
      });
    },
  });

  const handleSave = async () => {
    if (!description.trim()) {
      toast.error("Please add a table description", {
        position: "bottom-right",
      });
      return;
    }

    const payload = {
      updated_table_description: description,
      columns: columns,
    };
    updateColumnsFn(payload);
  };

  if (queryError) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="p-7 w-full max-w-4xl">
          <div className="text-red-500 text-center py-4">
            Error loading table data. Please try again.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="p-7 w-full max-w-4xl">
        <DialogHeader className="justify-start items-start">
          <DialogTitle className="text-2xl font-medium">
            Table Details
          </DialogTitle>
          <div
            className={`flex items-center justify-center space-x-2 ${
              isLoading || (isFetching && "pb-5")
            }`}
          >
            {tableName && (
              <h4 className="inline-flex items-center gap-1">
                <span>
                  <TableIcon size={20} className="text-orange-500" />
                </span>
                {tableName.split("_").join(" ")}
              </h4>
            )}
          </div>

          {isLoading || isFetching ? (
            <Skeleton className="w-full h-10" />
          ) : (
            <div className="flex items-center gap-1 w-full pr-2 pt-5">
              <Input
                className="text-sm w-full focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0"
                value={description}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value.length <= 400) {
                    setDescription(value);
                  }
                }}
                placeholder="Add table description (Up to 400 characters)"
              />
              <span className="text-xs font-medium text-gray-400 w-20 h-10 flex items-center justify-center rounded-md bg-slate-100 dark:bg-neutral-800 dark:text-gray-200 mr-0.5">
                <span className="text-gray-600 font-semibold pr-1 dark:text-white">
                  {description?.length}
                </span>
                / 400
              </span>
            </div>
          )}
        </DialogHeader>

        {isLoading || isFetching ? (
          <SkeletonLoader />
        ) : (
          <ScrollArea className="overflow-auto max-h-96">
            <Table className="w-full min-w-max">
              <TableHeader className="bg-slate-100 dark:bg-neutral-800 ">
                <TableRow className="dark:border-slate-800">
                  <TableHead className="w-24 text-black text-base font-semibold">
                    Name
                  </TableHead>
                  <TableHead className="w-24 text-black text-base font-semibold">
                    Type
                  </TableHead>
                  <TableHead className="flex-1 text-black text-base font-semibold">
                    Description
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {columns.map((item, index) => (
                  <TableRow
                    key={item.column_name}
                    className="dark:border-neutral-700"
                  >
                    <TableCell className="font-medium text-sm p-2 w-24 truncate">
                      {item.column_name.split("_").join(" ")}
                    </TableCell>
                    <TableCell className="p-2 font-medium text-sm w-36">
                      {item.data_type}
                    </TableCell>
                    <TableCell className="font-medium p-2 flex-1 pr-3">
                      <div className="flex items-center gap-1 w-full">
                        <Input
                          placeholder="Add column description (Up to 400 characters)"
                          className="w-full focus:outline-none focus:ring-0 focus-visible:ring-0 focus-visible:outline-none focus-visible:ring-offset-0"
                          value={item.column_description || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value.length <= 400) {
                              const newColumns = [...columns];
                              newColumns[index].column_description = value;
                              setColumns(newColumns);
                            }
                          }}
                        />
                        <span className="text-xs font-medium text-gray-400 w-20 h-10 flex items-center justify-center rounded-md bg-slate-100 dark:bg-neutral-800 dark:text-gray-200">
                          <span className="text-gray-600 font-semibold pr-1 dark:text-white">
                            {item?.column_description?.length}
                          </span>
                          / 400
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </ScrollArea>
        )}

        {isError && (
          <div className="text-red-500 mt-2">{mutationError.message}</div>
        )}

        <DialogFooter className="pt-5">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isPending}
            className="dark:text-white"
          >
            Cancel
          </Button>
          <Button
            disabled={isPending}
            className={`${
              !isPending ? "px-6" : ""
            } bg-orange-500 dark:bg-orange-500 dark:text-white`}
            onClick={handleSave}
          >
            {isPending ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddTableSchema;

//scrollarea skeleton loader
const SkeletonLoader = () => {
  return (
    <div className="space-y-4">
      <div className="flex gap-4">
        <Skeleton className="h-10 w-24 rounded" />
        <Skeleton className="h-10 w-24 rounded" />
        <Skeleton className="h-10 flex-1 rounded" />
      </div>
      {[...Array(6)].map((_, index) => (
        <div key={index} className="flex gap-4">
          <Skeleton className="h-10 w-24 rounded" />
          <Skeleton className="h-10 w-24 rounded" />
          <Skeleton className="h-10 flex-1 rounded" />
        </div>
      ))}
    </div>
  );
};
